import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Sidebar as SidebarRoot, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupLabel, 
  SidebarGroupContent, 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton,
  useSidebar
} from '@/components/ui/sidebar';
import { 
  AuditorSidebarConfig, 
  DEFAULT_AUDITOR_CONFIG, 
  getEnabledTabs 
} from './auditor-sidebar-config';

interface AuditorSidebarProps {
  config?: AuditorSidebarConfig;
  className?: string;
}

export function AuditorSidebar({ config = DEFAULT_AUDITOR_CONFIG, className = '' }: AuditorSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const { open } = useSidebar();
  
  // Get enabled tabs from config
  const enabledTabs = getEnabledTabs(config);
  
  // Determine active tab from current location
  const currentPath = location.pathname;
  
  // Special cases for detail pages - highlight the appropriate register tab
  let activeTab;
  if (currentPath.includes('asset-detail')) {
    activeTab = 'asset-register';
  } else if (currentPath.includes('risk-detail')) {
    activeTab = 'risk-register';
  } else if (currentPath.includes('document-detail')) {
    activeTab = 'document-library';
  } else {
    // Regular tab matching logic
    activeTab = enabledTabs.find(tab => currentPath.includes(tab.key))?.key || enabledTabs[0]?.key;
  }
  
  // Handle tab navigation
  const handleTabClick = (tab: string, path: string) => {
    navigate(path);
  };
  
  return (
    <SidebarRoot className={`h-full min-h-screen flex flex-col bg-card border-r border-border ${className}`}>
      <SidebarContent className="flex-1 h-full">
        <div className="h-14 p-4 border-b border-border">
          <div className="flex items-center">
            <h1 className="text-xl font-semibold text-foreground">Auditor View</h1>
          </div>
        </div>
        
        <SidebarGroup>
          <SidebarGroupLabel className="text-foreground">Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {enabledTabs.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={activeTab === item.key}
                  >
                    <button 
                      onClick={() => handleTabClick(item.key, item.path)} 
                      className="auditor-sidebar-item flex items-center gap-3 w-full text-left py-2 px-3 rounded-md transition-colors"
                      data-active={activeTab === item.key}
                    >
                      <FontAwesomeIcon 
                        icon={item.icon} 
                        className={`w-4 h-4 ${activeTab === item.key ? 'text-primary' : 'text-muted-foreground'}`} 
                      />
                      <span className={activeTab === item.key ? 'text-primary font-medium' : 'text-muted-foreground'}>
                        {item.label}
                      </span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </SidebarRoot>
  );
}
